import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Select, Space, Table, notification } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase-config';
import { adminGrantCompany } from '../../api/admin';
import { levelOptions } from './accessLevelHelp';

type UserCompanyMembersModalProps = {
  open: boolean;
  onCancel: () => void;
  company?: any | null;
  onChanged?: () => void;
};

const UserCompanyMembersModal: React.FC<UserCompanyMembersModalProps> = ({ open, onCancel, company, onChanged }) => {
  const [userMap, setUserMap] = useState<Record<string, any>>({});
  const [loadingIds, setLoadingIds] = useState<Record<string, boolean>>({});

  const [accessLevels, setAccessLevels] = useState<Record<string, number>>({});

  useEffect(() => {
    setAccessLevels(company?.accessLevels || {});
  }, [company]);

  const entries = useMemo(() => {
    return Object.keys(accessLevels).map(uid => ({ uid, level: accessLevels[uid] }));
  }, [accessLevels]);

  useEffect(() => {
    if (!open || entries.length === 0) return;
    const missingIds = entries.map(e => e.uid).filter(uid => !(uid in userMap));
    if (missingIds.length === 0) return;

    const loadUsers = async () => {
      const next = { ...userMap };
      const chunkSize = 10;
      for (let i = 0; i < missingIds.length; i += chunkSize) {
        const chunk = missingIds.slice(i, i + chunkSize);
        const q = query(collection(db, 'users'), where(documentId(), 'in', chunk));
        const snap = await getDocs(q);
        snap.docs.forEach(doc => {
          next[doc.id] = { id: doc.id, ...doc.data() };
        });
        chunk.forEach(id => {
          if (!next[id]) next[id] = { id };
        });
      }
      setUserMap(next);
    };

    loadUsers().catch((e) => {
      notification.error({
        message: 'Failed to load users',
        description: e?.message || String(e),
        placement: 'topRight',
      });
    });
  }, [entries, open, userMap]);

  const handleUpdate = async (uid: string, level: number) => {
    if (!company?.id) return;
    try {
      setLoadingIds(prev => ({ ...prev, [uid]: true }));
      await adminGrantCompany({ uid, companyId: company.id, level });
      notification.success({
        message: 'Access updated',
        placement: 'topRight',
      });
      setAccessLevels(prev => {
        const next = { ...prev };
        if (level <= 0) {
          delete next[uid];
        } else {
          next[uid] = level;
        }
        return next;
      });
      onChanged?.();
    } catch (e: any) {
      notification.error({
        message: 'Update failed',
        description: e?.message || String(e),
        placement: 'topRight',
      });
    } finally {
      setLoadingIds(prev => ({ ...prev, [uid]: false }));
    }
  };

  const handleRemove = async (uid: string) => {
    await handleUpdate(uid, 0);
  };

  const columns = [
    {
      title: 'User',
      render: (_: any, row: any) => {
        const u = userMap[row.uid] || {};
        const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
        return u.email ? `${u.email}${name ? ` — ${name}` : ''}` : row.uid;
      },
    },
    {
      title: 'Level',
      render: (_: any, row: any) => (
        <Select
          value={row.level}
          options={levelOptions}
          style={{ width: 160 }}
          onChange={(v) => handleUpdate(row.uid, v)}
        />
      ),
    },
    {
      title: 'Actions',
      render: (_: any, row: any) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          loading={!!loadingIds[row.uid]}
          onClick={() => handleRemove(row.uid)}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title={`Company Users${company?.name ? ` — ${company.name}` : ''}`}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={720}
    >
      <Table
        size="small"
        rowKey={(r) => r.uid}
        columns={columns as any}
        dataSource={entries}
        pagination={false}
      />
    </Modal>
  );
};

export default UserCompanyMembersModal;
